/**
 * Firestore writes for guided opportunity qualification (Phase 4 increment 2).
 * Rules (firestore.rules → opportunities) validate the qualification map shape and actor.
 */
import { doc, getDoc, updateDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db, auth } from './firebase';
import { collectionPath } from './firestoreCollections';
import { generateTaskId } from './documentIdUtils';
import { allocateTaskNumber } from './associationUtils';
import { logEntityChanges } from './logger';
import { sanitizeString } from './sanitization';
import { QUALIFICATION_ITEMS, buildQualificationEntry } from './opportunityQualification';

const describe = entry => entry ? `${entry.status === 'confirmed' ? 'Confirmed' : 'Not known yet'}${entry.answer ? `: ${entry.answer}` : ''}` : '';

/**
 * Saves one reviewed answer to `opportunity.qualification.{key}`.
 * @returns {Promise<Object>} the entry as stored on the server (with its server `updatedAt`), so callers
 *   that later rewrite the whole document (OppModal) send back an unchanged value
 */
export async function saveQualificationAnswer(opp, key, input, user) {
  const item = QUALIFICATION_ITEMS.find(def => def.key === key);
  if (!item) throw new Error(`Unknown qualification item: ${key}`);
  const entry = buildQualificationEntry(input, { authUid: auth.currentUser?.uid, user });
  const ref = doc(db, collectionPath('opportunities'), opp.id);
  await updateDoc(ref, {
    [`qualification.${key}`]: { ...entry, updatedAt: serverTimestamp() },
    updatedAt: serverTimestamp()
  });
  const field = `Qualification: ${item.label}`;
  await logEntityChanges({
    oldObj: { [field]: describe(opp.qualification?.[key]) },
    newObj: { [field]: describe(entry) },
    fields: [field],
    entityType: 'Opportunity',
    entityId: opp.id,
    user,
    action: 'Updated',
    extraDetails: `Guided qualification${entry.actedAsUid ? ` (via View As by ${entry.updatedByUid})` : ''}`
  });
  const stored = (await getDoc(ref)).data()?.qualification?.[key];
  return stored || { ...entry, updatedAt: new Date() };
}

/** Creates a follow-up task from TaskModal output for one qualification question. */
export async function createQualificationTask(opp, key, taskData, user) {
  const seq = await allocateTaskNumber();
  const taskDocId = generateTaskId(seq);
  const dueDate = taskData.dueDate instanceof Date ? Timestamp.fromDate(taskData.dueDate) : taskData.dueDate;
  const task = {
    ...taskData,
    title: sanitizeString(taskData.title || '', 200),
    description: sanitizeString(taskData.description || '', 5000),
    taskNumber: seq,
    taskNumberLabel: taskDocId,
    status: 'open',
    priority: taskData.priority || 'Medium',
    dueDate,
    customerId: taskData.customerId || opp.customerId || '',
    opportunityId: taskData.opportunityId || opp.id,
    associations: taskData.associations || {
      customerId: opp.customerId || null,
      customerName: opp.customerName || null,
      opportunityId: opp.id
    },
    source: 'opportunity_qualification',
    qualificationKey: key,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  await setDoc(doc(db, collectionPath('tasks'), taskDocId), task);
  await logEntityChanges({
    oldObj: null,
    newObj: { ...task, dueDate },
    fields: ['title', 'status', 'priority', 'uid', 'dueDate', 'customerId', 'opportunityId', 'description'],
    entityType: 'Task',
    entityId: taskDocId,
    user,
    action: 'Created',
    extraDetails: `Created from guided qualification on ${opp.opportunityNumberLabel || opp.id}`
  });
  return taskDocId;
}
